import { AgXToneMapping, SRGBColorSpace, type ToneMapping } from "three";
import type { RenderScope } from "../app/renderConfig.ts";
import type { ViewMode } from "../app/storeTypes.ts";

export interface DisplayColorPipelineTarget {
  outputColorSpace?: string;
  toneMapping: ToneMapping;
  toneMappingExposure: number;
}

export interface AmbientFillInput {
  scope: RenderScope;
  viewMode: ViewMode;
  envMapEnabled: boolean;
  ambientColor: string;
  ambientIntensity: number;
}

export interface AmbientFill {
  color: string;
  intensity: number;
}

export const DISPLAY_OUTPUT_COLOR_SPACE = SRGBColorSpace;
export const DISPLAY_TONE_MAPPING = AgXToneMapping;
export const DISPLAY_TONE_MAPPING_EXPOSURE = 1.0;
export const SHADOW_CATCHER_OPACITY = 0.2;

export function applyDisplayColorPipeline(
  renderer: DisplayColorPipelineTarget,
): void {
  if ("outputColorSpace" in renderer) {
    renderer.outputColorSpace = DISPLAY_OUTPUT_COLOR_SPACE;
  }
  renderer.toneMapping = DISPLAY_TONE_MAPPING;
  renderer.toneMappingExposure = DISPLAY_TONE_MAPPING_EXPOSURE;
}

export function isLitViewMode(viewMode: ViewMode): boolean {
  return viewMode === "material";
}

export function resolveAmbientFill(
  input: AmbientFillInput,
): AmbientFill | null {
  if (!isLitViewMode(input.viewMode) || input.ambientIntensity <= 0) {
    return null;
  }

  // In render lookdev, the HDR environment should define the baseline light
  // response. Extra hemispherical fill flattens contact and form, so when IBL
  // is active we remove the global ambient term entirely.
  if (input.scope === "render" && input.envMapEnabled) {
    return null;
  }

  // Viewport creation modes still benefit from a small amount of fill to keep
  // silhouettes legible while orbiting, but HDRI should remain dominant.
  const intensity =
    input.envMapEnabled && input.scope === "viewport"
      ? input.ambientIntensity * 0.35
      : input.ambientIntensity;

  if (intensity <= 1e-4) return null;

  return {
    color: input.ambientColor,
    intensity,
  };
}
